import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AwaitReturn, TutorialState, TUTORIAL_VERSION } from './tutorialTypes';

const initialState: TutorialState = {
  enabled: true,
  step: 'HOME_STONE_1',
  awaitReturn: null,
  version: TUTORIAL_VERSION,
  complete: false,
  manual: false,
};

type HydratePayload = Partial<TutorialState> | null;

const tutorialSlice = createSlice({
  name: 'tutorial',
  initialState,
  reducers: {
    hydrate(state, action: PayloadAction<HydratePayload>) {
      const data = action.payload;
      if (!data) {
        return;
      }
      if (data.version !== TUTORIAL_VERSION) {
        return;
      }
      state.enabled = data.complete ? false : (data.enabled ?? state.enabled);
      state.step = data.step ?? state.step;
      state.awaitReturn = (data.awaitReturn as AwaitReturn) ?? state.awaitReturn;
      state.version = TUTORIAL_VERSION;
      state.complete = data.complete ?? state.complete;
    },

    start(state, action: PayloadAction<boolean>) {
      state.enabled = action.payload;
      state.step = 'HOME_STONE_1';
      state.awaitReturn = null;
      state.version = TUTORIAL_VERSION;
      state.complete = !action.payload;
      state.manual = action.payload;
    },

    skip(state) {
      state.enabled = false;
      state.step = 'DONE';
      state.awaitReturn = null;
      state.complete = true;
    },

    onStoneClick(state) {
      if (!state.enabled || state.step !== 'HOME_STONE_1') {
        return;
      }
      state.awaitReturn = 'STONE';
    },

    onSettlementClick(state) {
      if (!state.enabled || state.step !== 'HOME_SETTLEMENT_1') {
        return;
      }
      state.awaitReturn = 'SETTLEMENT';
    },

    onHomeMounted(state) {
      if (!state.enabled) {
        return;
      }

      if (state.awaitReturn === 'STONE') {
        state.step = 'HOME_SETTLEMENT_1';
        state.awaitReturn = null;
      } else if (state.awaitReturn === 'SETTLEMENT') {
        state.step = 'HOME_CALC_1';
        state.awaitReturn = null;
      }
    },

    onCalculatorClick(state) {
      if (!state.enabled || state.step !== 'HOME_CALC_1') {
        return;
      }
      state.step = 'CALC_1';
    },

    nextCalcStep(state) {
      if (!state.enabled) {
        return;
      }

      if (state.step === 'CALC_1') {
        state.step = 'CALC_2';
      } else if (state.step === 'CALC_2') {
        state.step = 'DONE';
        state.enabled = false;
        state.awaitReturn = null;
        state.complete = true;
      }
    },

    nextStep(state) {
      if (!state.enabled) {
        return;
      }

      if (state.step === 'HOME_STONE_1') {
        state.step = 'HOME_SETTLEMENT_1';
        state.awaitReturn = null;
      } else if (state.step === 'HOME_SETTLEMENT_1') {
        state.step = 'HOME_CALC_1';
        state.awaitReturn = null;
      } else if (state.step === 'HOME_CALC_1') {
        state.step = 'CALC_1';
        state.awaitReturn = null;
      } else if (state.step === 'CALC_1') {
        state.step = 'CALC_2';
        state.awaitReturn = null;
      } else if (state.step === 'CALC_2') {
        state.step = 'DONE';
        state.enabled = false;
        state.awaitReturn = null;
        state.complete = true;
      }
    },
    prevStep(state) {
      if (!state.enabled) {
        return;
      }

      if (state.step === 'HOME_SETTLEMENT_1') {
        state.step = 'HOME_STONE_1';
        state.awaitReturn = null;
      } else if (state.step === 'HOME_CALC_1') {
        state.step = 'HOME_SETTLEMENT_1';
        state.awaitReturn = null;
      } else if (state.step === 'CALC_1') {
        state.step = 'HOME_CALC_1';
        state.awaitReturn = null;
      } else if (state.step === 'CALC_2') {
        state.step = 'CALC_1';
        state.awaitReturn = null;
      }
    },
  },
});

export const tutorialActions = tutorialSlice.actions;
export const tutorialReducer = tutorialSlice.reducer;
